import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { StyledColorInput } from "./styles";

import { convertPxToRem } from "frontend/components/helpers";
import { isHex } from "./helpers";
import { useIconEditorDispatch } from "js/contexts/IconEditorContext";

import {
    convertHexToRgb,
    convertRgbToHex,
    convertRgbToNum,
    isRgbValid,
    formatRgb
} from "icons/IconEditor/helpers";

const RgbColorInput = ({ setActiveColor, colorTextInputValue, background }) => {
    const [rgbInput, setRgbInput] = useState("");

    const dispatch = useIconEditorDispatch();

    // while a valid hex will usually always be present, this will allow
    // the component to take the initial Hex on load
    const inputVal =
        rgbInput.length > 0
            ? rgbInput
            : convertHexToRgb({ hexCode: colorTextInputValue });

    useEffect(() => {
        // if incoming colorTextInputValue from parent is empty it means its Transparent
        if (colorTextInputValue.length === 0) {
            setRgbInput("");
        } else if (isHex.test(colorTextInputValue)) {
            const convert = convertHexToRgb({ hexCode: colorTextInputValue });
            const formatted = formatRgb(convert);
            setRgbInput(formatted);
        }
    }, [colorTextInputValue]);

    const sendToReducer = ({ payload }) => {
        const actionType = background
            ? "UPDATE_BACKGROUND_COLOR_TEXT_INPUT_VALUE"
            : "UPDATE_ICON_COLOR_TEXT_INPUT_VALUE";

        // this will allow the user to keep typing but prevent the main state (hex value) from being updated
        // technically if a user is inputting "123,123" that is an invalid RGB but #123123 is a real Hex.
        // until the payload is a valid RGB and can be converted to Hex for the main state we dont update
        let finalPayload;

        const isValidRgb = isRgbValid(payload);
        const isEmptyStr = payload.length === 0;

        if (isValidRgb || isEmptyStr) {
            if (isValidRgb) {
                // first translate "123, 12, 1" to [123, 12, 1]
                const normalizeStrInputToArrOfVals = convertRgbToNum({
                    rgb: payload
                });

                // then convert [123, 12, 1] to #7B0C01
                finalPayload = convertRgbToHex({
                    r: normalizeStrInputToArrOfVals[0],
                    g: normalizeStrInputToArrOfVals[1],
                    b: normalizeStrInputToArrOfVals[2]
                });

                // update state. activeColor is the circle swatch left of the hex input
                setActiveColor({ color: finalPayload });
            } else if (isEmptyStr) {
                finalPayload = "";
            }

            dispatch({
                type: actionType,
                payload: finalPayload
            });
        }
    };

    const updateInput = str => {
        const numOfCommas = str.split(",").length - 1;
        if (numOfCommas === 3) return;

        // this makes sure user can only input numbers and commas for RGB
        const hasInvalidChar = /[^\d,\s]+/.test(str);

        if (!hasInvalidChar) {
            setRgbInput(str);
            sendToReducer({ payload: str });
        }
    };

    const onRgbBlur = val => {
        const isRGB = isRgbValid(val);
        if (isRGB) {
            setRgbInput(formatRgb(val));
        } else {
            setRgbInput("");
            sendToReducer({ payload: "" });
        }
    };

    return (
        <StyledColorInput
            value={inputVal}
            onChange={e => updateInput(e.target.value)}
            onBlur={e => onRgbBlur(e.target.value)}
            aria-label="selected RGB color"
            maxLength={13}
            width={convertPxToRem({ px: 100 })}
            placeholder="R, G, B"
        />
    );
};

RgbColorInput.propTypes = {
    setActiveColor: PropTypes.func.isRequired,
    colorTextInputValue: PropTypes.string,
    background: PropTypes.bool
};

export default RgbColorInput;
